import Button from "../ui/Button.jsx";
import useReminders from "../../context/useReminders.js";

/**
 * ReminderBanner — the permission-free half of the reminder pipeline.
 *
 * A stack of persistent, glowing cards pinned to the top of the app shell
 * (mounted by AppLayout, inside ReminderProvider). It renders straight from
 * `useAppointmentReminders().alerts`, so a reminder is on screen the moment the
 * poller raises it — even when the browser's native Notification permission is
 * denied or blocked, and even when the tab is not focused (staff can glance
 * back at a counter screen and still see it).
 *
 * Cards never auto-dismiss: staff clear them with [View appointment] /
 * [Dismiss], so a reminder cannot silently scroll or time itself off the
 * screen. `role="alert"` + `aria-live="assertive"` announce each addition to
 * screen readers, and the gold glow animation keeps the card catching the eye
 * while it is pending (both animations bow out under `prefers-reduced-motion`).
 */

/**
 * Deep link into the schedule: the appointments page reads `?tab=` for the
 * schedule slice and `?q=` for the client search box (prefilled at mount), so
 * "View appointment" lands on the row that lists this booking.
 */
const appointmentHref = (alert) => {
  const params = new URLSearchParams({ tab: alert.tab ?? "today" });
  if (alert.customerName) params.set("q", alert.customerName);
  return `/appointments?${params.toString()}`;
};

function ReminderCard({ alert, onDismiss }) {
  const isTest = alert.kind === "test";

  return (
    <div className="w-full animate-slide-down motion-reduce:animate-none">
      {/* The glow loop lives on an inner element so the entrance animation and
          the pulse never fight over the same `animation` shorthand. */}
      <div className="animate-reminder-glow motion-reduce:animate-none overflow-hidden rounded-2xl border border-primary-container/50 bg-surface-container-lowest shadow-float">
        <div className="gold-gradient flex items-center gap-3 px-4 py-2.5 text-on-primary">
          <span
            className="material-symbols-outlined material-filled text-lg"
            aria-hidden="true"
          >
            {isTest ? "notifications_active" : "alarm"}
          </span>
          <p className="flex-1 truncate font-headline text-xs font-bold uppercase tracking-widest">
            {isTest ? "Test reminder alert" : "Appointment reminder"}
          </p>
          <button
            type="button"
            onClick={onDismiss}
            aria-label={`Dismiss reminder for ${alert.customerName}`}
            className="-mr-1 flex h-8 w-8 items-center justify-center rounded-full transition-all duration-200 hover:bg-white/15 active:scale-90"
          >
            <span className="material-symbols-outlined text-lg" aria-hidden="true">
              close
            </span>
          </button>
        </div>

        <div className="flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-center">
          <div className="min-w-0 flex-1">
            <p className="truncate font-headline text-lg font-bold text-on-surface">
              {alert.customerName}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-on-surface-variant">
              <span className="inline-flex min-w-0 items-center gap-1.5">
                <span
                  className="material-symbols-outlined shrink-0 text-base"
                  aria-hidden="true"
                >
                  content_cut
                </span>
                <span className="truncate">{alert.serviceName}</span>
              </span>
              <span className="inline-flex items-center gap-1.5 font-bold text-primary">
                <span
                  className="material-symbols-outlined text-base"
                  aria-hidden="true"
                >
                  schedule
                </span>
                {alert.whenLabel}
              </span>
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap justify-end gap-3">
            {!isTest && (
              <Button
                size="sm"
                icon="event"
                to={appointmentHref(alert)}
                onClick={onDismiss}
              >
                View appointment
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={onDismiss}>
              Dismiss
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ReminderBanner() {
  const reminders = useReminders();
  const alerts = reminders?.alerts ?? [];

  // No provider / nothing due: render nothing at all, so the shell never
  // reserves space (or swallows clicks) for an empty drawer.
  if (!reminders || alerts.length === 0) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-label="Appointment reminders"
      className="pointer-events-none fixed inset-x-0 top-0 z-[70] flex justify-center px-4 pt-4 sm:px-6 lg:left-72"
    >
      <div className="pointer-events-auto flex max-h-[80vh] w-full max-w-2xl flex-col gap-3 overflow-y-auto overscroll-contain rounded-2xl">
        {alerts.map((alert) => (
          <ReminderCard
            key={alert.id}
            alert={alert}
            onDismiss={() => reminders.dismissAlert(alert.id)}
          />
        ))}

        {alerts.length > 1 && (
          <div className="sticky bottom-0 flex shrink-0 justify-center pb-1">
            <button
              type="button"
              onClick={reminders.dismissAllAlerts}
              className="rounded-full bg-inverse-surface/90 px-4 py-2 text-xs font-bold text-inverse-on-surface shadow-ambient transition-all duration-200 hover:bg-inverse-surface active:scale-95"
            >
              Dismiss all ({alerts.length})
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
